import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AttachmentView } from "@/components/AttachmentView";
import { MessageReactions } from "@/components/MessageReactions";
import { useCall } from "@/components/CallManager";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Send, Trash2, Paperclip, X, Loader2, Eye, Users2, Phone, Video, PhoneCall } from "lucide-react";

const initials = (n = "") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const timeOf = (ts) => { try { return new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export const GroupChatPanel = ({ groupId, onDiscarded }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { startCall, startHuddle, joinRoom, inCall } = useCall() || {};
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const typingRef = useRef(0);

  const { data } = useQuery({
    queryKey: ["group-messages", groupId],
    queryFn: async () => (await api.get(`/chat/groups/${groupId}/messages`)).data,
    refetchInterval: 4000,
  });
  const group = data?.group;
  const messages = data?.messages || [];
  const readOnly = data?.read_only;

  const { data: typingData } = useQuery({
    queryKey: ["group-typing", groupId],
    queryFn: async () => (await api.get(`/chat/groups/${groupId}/typing`)).data,
    refetchInterval: 2000,
  });
  const typingNames = typingData?.typing || [];

  const { data: activeRoom } = useQuery({
    queryKey: ["active-room", groupId],
    queryFn: async () => (await api.get(`/calls/rooms/active`, { params: { group_id: groupId } })).data,
    refetchInterval: 5000,
    enabled: !!group?.active,
  });
  const liveHuddle = activeRoom?.room_id ? activeRoom : null;
  const typingLabel = typingNames.length === 1 ? `${typingNames[0]} is typing…`
    : typingNames.length === 2 ? `${typingNames[0]} and ${typingNames[1]} are typing…`
    : typingNames.length > 2 ? "Several people are typing…" : "";

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (!readOnly && now - typingRef.current > 2000) {
      typingRef.current = now;
      api.post(`/chat/groups/${groupId}/typing`).catch(() => {});
    }
  };
  const canDiscard = group && (group.created_by === user?.user_id || ["founder", "admin"].includes(user?.role)) && group.active;

  const seen = group?.seen || {};
  const nameOf = {};
  (group?.member_ids || []).forEach((id, i) => { nameOf[id] = (group?.member_names || [])[i] || ""; });

  useEffect(() => {
    if (data && !readOnly) api.post(`/chat/groups/${groupId}/seen`).catch(() => {});
  }, [messages.length, readOnly, groupId]); // eslint-disable-line

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages.length, groupId]);

  const send = useMutation({
    mutationFn: () => api.post(`/chat/groups/${groupId}/messages`, { text: text.trim(), attachment: pendingFile || undefined }),
    onSuccess: () => { setText(""); setPendingFile(null); qc.invalidateQueries({ queryKey: ["group-messages", groupId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send."),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/chat/messages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-messages", groupId] }),
  });
  const discard = useMutation({
    mutationFn: () => api.delete(`/chat/groups/${groupId}`),
    onSuccess: () => { toast.success("Group discarded."); qc.invalidateQueries({ queryKey: ["chat-groups"] }); onDiscarded?.(); },
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingFile(res.data);
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = (e) => { e?.preventDefault(); if ((text.trim() || pendingFile) && !send.isPending && !uploading) send.mutate(); };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 210px)" }} data-testid="group-chat-panel">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-navy" />
          <span className="font-heading text-sm font-semibold text-navy">{group?.name || "Group"}</span>
          {group && !group.active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">Discarded</span>}
          <span className="text-[11px] text-slate-400">{group?.member_names?.filter(Boolean).join(", ")}</span>
        </div>
        <div className="flex items-center gap-2">
          {group && group.active && liveHuddle && !inCall && (
            <Button size="sm" onClick={() => joinRoom?.(liveHuddle.room_id, liveHuddle.media)} data-testid="join-huddle-btn" className="h-7 animate-pulse rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
              <PhoneCall className="mr-1 h-3.5 w-3.5" /> Join ({liveHuddle.count})
            </Button>
          )}
          {group && group.active && (group.member_ids || []).some((id) => id !== user?.user_id) && !inCall && (
            <Button size="sm" variant="outline" onClick={() => startHuddle?.(groupId, "video")} data-testid="start-huddle-btn" className="h-7 rounded-lg border-navy/20 text-navy hover:bg-navy/5">
              <Users2 className="mr-1 h-3.5 w-3.5" /> Huddle
            </Button>
          )}
          {group && group.active && (group.member_ids || []).some((id) => id !== user?.user_id) && (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" data-testid="group-call-btn" className="h-7 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <Phone className="mr-1 h-3.5 w-3.5" /> Call
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-1" data-testid="call-member-list">
                <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ring a member</div>
                {(group.member_ids || []).map((id, i) => {
                  if (id === user?.user_id) return null;
                  const nm = (group.member_names || [])[i] || "Member";
                  return (
                    <div key={id} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50" data-testid={`call-row-${id}`}>
                      <span className="truncate text-sm text-navy">{nm}</span>
                      <span className="flex gap-1">
                        <button onClick={() => startCall?.(groupId, id, nm, "audio")} data-testid={`call-audio-${id}`} title="Audio call" className="rounded-full p-1.5 text-emerald-600 hover:bg-emerald-50"><Phone className="h-4 w-4" /></button>
                        <button onClick={() => startCall?.(groupId, id, nm, "video")} data-testid={`call-video-${id}`} title="Video call" className="rounded-full p-1.5 text-navy hover:bg-navy/5"><Video className="h-4 w-4" /></button>
                      </span>
                    </div>
                  );
                })}
              </PopoverContent>
            </Popover>
          )}
          {canDiscard && (
            <Button size="sm" variant="outline" onClick={() => discard.mutate()} data-testid="discard-group-btn" className="h-7 rounded-lg border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Discard
            </Button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 bg-amber-50 px-4 py-1.5 text-[11px] font-medium text-amber-700" data-testid="oversight-banner">
          <Eye className="h-3.5 w-3.5" /> Oversight view — read only. Members are not notified you are here.
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 px-3 py-4" data-testid="group-messages">
        {messages.length === 0 && <div className="flex h-full items-center justify-center text-sm text-slate-400">No messages yet.</div>}
        {messages.map((m) => {
          const mine = m.user_id === user?.user_id;
          const seenNames = mine
            ? (group?.member_ids || []).filter((uid) => uid !== user?.user_id && seen[uid] && seen[uid] >= m.created_at).map((uid) => nameOf[uid] || "Someone")
            : [];
          return (
            <div key={m.message_id} className={cn("flex items-end gap-2", mine ? "flex-row-reverse" : "")} data-testid={`group-msg-${m.message_id}`}>
              {!mine && <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-gold">{initials(m.name)}</div>}
              <div className={cn("group max-w-[78%] rounded-2xl px-3 py-2 shadow-sm", mine ? "bg-navy text-white" : "bg-white text-navy")}>
                {!mine && <div className="mb-0.5 text-xs font-semibold text-navy">{m.name}</div>}
                {m.text && <p className={cn("whitespace-pre-wrap break-words text-sm", mine ? "text-white" : "text-slate-700")}>{m.text}</p>}
                {m.attachment && <AttachmentView attachment={m.attachment} />}
                <MessageReactions message={m} invalidateKey={["group-messages", groupId]} dark={mine} />
                <div className={cn("mt-1 flex items-center gap-2", mine ? "justify-end" : "")}>
                  <span className={cn("text-[10px]", mine ? "text-white/60" : "text-slate-400")}>{timeOf(m.created_at)}</span>
                  {mine && seenNames.length > 0 && (
                    <span className="text-[10px] text-white/70" data-testid={`seen-${m.message_id}`} title={`Seen by ${seenNames.join(", ")}`}>
                      ✓ Seen by {seenNames.length === 1 ? seenNames[0] : `${seenNames.length}`}
                    </span>
                  )}
                  {(mine || ["founder", "admin"].includes(user?.role)) && (
                    <button onClick={() => remove.mutate(m.message_id)} data-testid={`group-delete-${m.message_id}`}
                      className={cn("opacity-0 transition-opacity group-hover:opacity-100", mine ? "text-white/70 hover:text-white" : "text-slate-300 hover:text-red-500")} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {typingLabel && (
        <div className="flex items-center gap-1.5 px-4 py-1 text-[11px] italic text-slate-400" data-testid="typing-indicator">
          <span className="flex gap-0.5">
            <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "0ms" }} />
            <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "150ms" }} />
            <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "300ms" }} />
          </span>
          {typingLabel}
        </div>
      )}

      {readOnly ? null : !group?.active ? (
        <div className="border-t border-slate-100 bg-white p-3 text-center text-xs text-slate-400">This group has been discarded.</div>
      ) : (
        <form onSubmit={submit} className="border-t border-slate-100 bg-white p-3">
          {pendingFile && (
            <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs text-navy" data-testid="group-pending-attachment">
              <Paperclip className="h-3.5 w-3.5" /><span className="max-w-[200px] truncate">{pendingFile.name}</span>
              <button type="button" onClick={() => setPendingFile(null)} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="group-file-input" accept="image/*,.pdf,.csv,.txt,.xlsx,.docx" />
            <Button type="button" variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="group-attach-btn" className="rounded-lg shrink-0">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
            <Input value={text} onChange={onType} data-testid="group-input" className="flex-1" maxLength={2000} placeholder="Message the group…" />
            <Button type="submit" disabled={(!text.trim() && !pendingFile) || send.isPending || uploading} data-testid="group-send" className="rounded-lg bg-navy text-white hover:bg-navy-900 shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
